import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import axios from "axios"

// Detección de ambiente en RUNTIME, no build-time (BDSJEA-222): el pipeline de
// CI nunca setea process.env.REACT_APP_ENV en el bundle deployado (solo los
// script start lo hace, para uso 100% local del developer), así
// que basarse en esa env var hacía que TODOS los ambientes deployados
// (dev/qa/uat/prod) cayeran siempre en modo "local" y armaran las URLs de
// healthcheck contra localhost del navegador del usuario en vez de contra el
// ambiente real. En su lugar, igual que el resto de la app (src/api.js,
// src/api/agro.js), resolvemos las URLs de los microservicios contra las
// window.REACT_*_URL inyectadas en runtime por public/config/env-config.js, y
// solo distinguimos "local" (developer corriendo `npm start`, microservicios
// sueltos por CORS en localhost:800X) mirando el hostname real del navegador.
const IS_LOCAL = ["localhost", "127.0.0.1"].includes(window.location.hostname)
const ENV_LABEL = IS_LOCAL ? "LOCAL" : window.location.hostname.toUpperCase()

const FETCH_TIMEOUT_MS = 15000
const AUTO_REFRESH_SECONDS = 30
const LATENCY_WARN_MS = 2000

// ewan-customservices es el único microservicio sin window.REACT_*_URL propia
// (el resto del frontend nunca lo consume directo desde el browser, por eso
// nunca se agregó al generador de env-config.js). En local corre suelto en su
// propio puerto (ver nota de CORS al pie del dashboard); en cualquier deploy
// (dev/qa/uat/prod) viaja por el mismo origen que los demás, ruteado por nginx
// bajo /ewancustomservices.
const CUSTOMSERVICES_LOCAL_PORT = 8003

const SERVICE_DEFS = [
  {
    id: "ewan-auth",
    name: "ewan-auth",
    description: "Autenticación & JWT",
    group: "core",
    basePath: "/ewanauth",
    healthPath: "/actuator/health",
    envUrlVar: "REACT_AUTH_URL",
  },
  {
    id: "ewan-api",
    name: "ewan-api",
    description: "API Gateway principal",
    group: "core",
    basePath: "/ewanapi",
    healthPath: "/actuator/health",
    envUrlVar: "REACT_APP_URL",
    latencyWarnMs: 10000,
  },
  {
    id: "ewan-customservices",
    name: "ewan-customservices",
    description: "Servicios personalizados",
    group: "core",
    basePath: "/ewancustomservices",
    healthPath: "/actuator/health",
    envUrlVar: null,
  },
  {
    id: "ewan-frontstyle",
    name: "ewan-frontstyle",
    description: "Assets & estilos frontend",
    group: "core",
    basePath: "/ewanfrontstyle",
    healthPath: "/actuator/health",
    envUrlVar: "REACT_CONFIG_URL",
  },
  {
    id: "ewan-agro",
    name: "ewan-agro",
    description: "Módulo agropecuario",
    group: "agro",
    basePath: "/ewanagro",
    healthPath: "/actuator/health",
    envUrlVar: "REACT_AGRO_URL",
  },
  // El migrador (Python, 8008) NO está expuesto por nginx y este dashboard
  // consulta desde el browser: su salud se lee de un endpoint de ewan-agro
  // (autenticado con JWT por el interceptor global de axios), no de su /health.
  // No suma al resumen general: es informativo y no debe poner en rojo al
  // sistema si el migrador no está levantado (p. ej. en local).
  {
    id: "ewan-migracion-bsj",
    name: "ewan-migracion-bsj (migrador)",
    description: "Migración BSJ (vía ewan-agro)",
    group: "migracion",
    kind: "migrador",
    basePath: "/ewanagro",
    healthPath: "/api/agro/migracion/salud",
    envUrlVar: "REACT_AGRO_URL",
    sinPuerto: true,
    fueraDelResumen: true,
  },
]

const MIGRADOR_MSG_SIN_RESPUESTA = "Sin respuesta del servicio de migración"
const MIGRADOR_MSG_SIN_SESION = "Iniciá sesión para ver el estado del migrador"
const MIGRADOR_MSG_SIN_PERMISO = "Sin permiso para ver el estado del migrador"
const MIGRADOR_MSG_DEGRADADO = "Servicio de migración degradado"
const MIGRADOR_MSG_DOWN = "Servicio de migración no disponible"
const MIGRADOR_MSG_DESHABILITADO = "Importación deshabilitada en este ambiente"
const MIGRADOR_ETIQUETA_DESHABILITADO = "DESHABILITADO"

// Base de ewan-customservices (sin variable propia). Si el front corre local
// pero REACT_APP_URL apunta a un ambiente remoto (ej. `npm run start:dev`
// contra develop en Azure), nginx lo expone bajo /ewancustomservices en ese
// mismo origen: se usa esa base. Si no, local puro (puerto suelto) o relativa.
function customservicesRemoteBase() {
  const appUrl = window.REACT_APP_URL
  if (!appUrl) return null
  try {
    const host = new URL(appUrl).hostname
    return ["localhost", "127.0.0.1"].includes(host) ? null : appUrl
  } catch {
    return null
  }
}

function buildBaseUrl(svc) {
  if (svc.envUrlVar) return window[svc.envUrlVar] || ""
  if (IS_LOCAL) {
    return (
      customservicesRemoteBase() ||
      "http://localhost:" + CUSTOMSERVICES_LOCAL_PORT
    )
  }
  return ""
}

function extractPort(urlString) {
  if (!urlString) return null
  try {
    return new URL(urlString).port || null
  } catch {
    return null
  }
}

const SERVICES = SERVICE_DEFS.map(function (s) {
  const port = s.sinPuerto
    ? null
    : s.envUrlVar
      ? extractPort(window[s.envUrlVar])
      : IS_LOCAL
        ? customservicesRemoteBase()
          ? extractPort(customservicesRemoteBase())
          : CUSTOMSERVICES_LOCAL_PORT
        : null
  return Object.assign({}, s, {
    port,
    baseUrl: buildBaseUrl(s),
  })
})

const STATUS = {
  healthy: "healthy",
  unhealthy: "unhealthy",
  down: "down",
  loading: "loading",
  // Neutro: no se pudo consultar por falta de sesión/permiso (no es una caída)
  neutral: "neutral",
}

const statusConfig = {
  healthy: {
    color: "#22c55e",
    glow: "0 0 12px #22c55e88",
    label: "ONLINE",
    bg: "#052e16",
  },
  unhealthy: {
    color: "#f59e0b",
    glow: "0 0 12px #f59e0b88",
    label: "DEGRADADO",
    bg: "#1c1403",
  },
  down: {
    color: "#ef4444",
    glow: "0 0 12px #ef444488",
    label: "CAÍDO",
    bg: "#1f0606",
  },
  loading: { color: "#6b7280", glow: "none", label: "...", bg: "#111" },
  neutral: { color: "#6b7280", glow: "none", label: "SIN ACCESO", bg: "#111" },
}

function mapActuatorStatus(actuatorStatus, httpStatus) {
  if (!actuatorStatus) return STATUS.down
  if (actuatorStatus === "UP") return STATUS.healthy
  if (actuatorStatus === "OUT_OF_SERVICE" || actuatorStatus === "UNKNOWN")
    return STATUS.unhealthy
  if (actuatorStatus === "DOWN" && httpStatus === 503) return STATUS.unhealthy
  return STATUS.down
}

function getHealthEntries(data) {
  if (!data) return null
  var entries = data.components || data.details || null
  if (entries) {
    entries = Object.fromEntries(
      Object.entries(entries).filter(
        ([k]) => k !== "discoveryComposite" && k !== "discovery",
      ),
    )
  }
  return entries
}

function hasUnhealthyComponent(data) {
  var entries = getHealthEntries(data)
  if (!entries) return false
  var watchedComponents = ["db", "diskSpace", "ping"]
  return watchedComponents.some(function (name) {
    var c = entries[name]
    return c && c.status && c.status !== "UP"
  })
}

async function fetchServiceHealth(
  service,
  previousHistory = [],
  extraFetch = null,
) {
  const healthUrl = `${service.baseUrl}${service.basePath}${service.healthPath}`
  const infoUrl = `${service.baseUrl}${service.basePath}/actuator/info`

  let data = null
  let versionInfo = null
  let status = STATUS.down
  let latency = null
  let errorMsg = null

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    const healthResponse = await fetch(healthUrl, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })

    const healthStart = Date.now()
    let healthText = ""
    try {
      healthText = await healthResponse.text()
      data = JSON.parse(healthText)
    } catch {
      /* body no es JSON válido o llegó incompleto */
    }
    latency = Date.now() - healthStart
    clearTimeout(timeoutId)

    const isWiremock =
      healthResponse.status === 200 &&
      data &&
      Array.isArray(data.mappings) &&
      !data.status
    const actuatorUp = (data && data.status === "UP") || isWiremock

    if (!actuatorUp) {
      if (data && data.status) {
        status = mapActuatorStatus(data.status, healthResponse.status)
      } else {
        status = STATUS.down
      }
    } else {
      status = STATUS.healthy
      if (hasUnhealthyComponent(data)) {
        status = STATUS.unhealthy
      }
      if (
        status === STATUS.healthy &&
        latency > (service.latencyWarnMs || LATENCY_WARN_MS)
      ) {
        status = STATUS.unhealthy
      }
    }

    const newHistory = [...previousHistory.slice(-19), status]

    let extraData
    if (extraFetch) {
      try {
        extraData = await extraFetch()
      } catch {
        extraData = null
      }
    }

    if (status === STATUS.healthy && extraData !== undefined) {
      if (extraData === null) {
        status = STATUS.down
      } else {
        const oneHourAgo = Date.now() - 3600000
        const ts = extraData.tsRegistro
          ? new Date(extraData.tsRegistro).getTime()
          : 0
        if (ts < oneHourAgo) {
          status = STATUS.unhealthy
        }
      }
      newHistory[newHistory.length - 1] = status
    }

    try {
      const infoController = new AbortController()
      const infoTimeoutId = setTimeout(
        () => infoController.abort(),
        FETCH_TIMEOUT_MS,
      )
      const infoResponse = await fetch(infoUrl, {
        signal: infoController.signal,
        headers: { Accept: "application/json" },
      })
      clearTimeout(infoTimeoutId)
      if (infoResponse.ok) {
        const infoText = await infoResponse.text()
        try {
          const infoJson = JSON.parse(infoText)
          versionInfo = infoJson.app?.version || infoJson.component || null
        } catch {
          versionInfo = null
        }
      }
    } catch {
      versionInfo = null
    }

    const uptime = Math.round(
      (newHistory.filter(s => s === STATUS.healthy).length /
        newHistory.length) *
        100,
    )

    return {
      ...service,
      status,
      latency,
      uptime,
      lastCheck: new Date(),
      history: newHistory,
      detail: data,
      error: null,
      extraData,
      versionInfo,
      dbStatus: data?.components?.db?.status || null,
      dbType: data?.components?.db?.details?.database || null,
      diskTotal: data?.components?.diskSpace?.details?.total || null,
      diskFree: data?.components?.diskSpace?.details?.free || null,
    }
  } catch (err) {
    var isTimeout = err.name === "AbortError"
    errorMsg = isTimeout
      ? "Timeout (>" + FETCH_TIMEOUT_MS + "ms)"
      : "Sin respuesta — " + err.message

    const newHistory = [...previousHistory.slice(-19), STATUS.down]
    const uptime = Math.round(
      (newHistory.filter(s => s === STATUS.healthy).length /
        newHistory.length) *
        100,
    )
    return {
      ...service,
      status: STATUS.down,
      latency: isTimeout ? FETCH_TIMEOUT_MS : null,
      uptime,
      lastCheck: new Date(),
      history: newHistory,
      detail: null,
      error: errorMsg,
      extraData: null,
      versionInfo: null,
    }
  }
}

function migradorShortSha(sha) {
  return typeof sha === "string" && sha ? sha.slice(0, 7) : null
}

function migradorEstadoToStatus(estado) {
  if (estado === "UP") return STATUS.healthy
  if (estado === "DEGRADADO") return STATUS.unhealthy
  // Importación deshabilitada a propósito (p. ej. PROD): neutro, no es una caída
  if (estado === "DESHABILITADO") return STATUS.neutral
  return STATUS.down
}

// Salud del migrador vía ewan-agro. Usa axios (el interceptor global agrega el
// JWT); a diferencia del resto de las cards, el endpoint requiere autenticación.
// Nunca expone host/URL/stacktrace: ante una falla muestra un texto fijo.
async function fetchMigradorHealth(service, previousHistory = []) {
  const url = `${service.baseUrl}${service.basePath}${service.healthPath}`
  const t0 = Date.now()
  const finish = (status, extra) => {
    const newHistory = [...previousHistory.slice(-19), status]
    const medibles = newHistory.filter(s => s !== STATUS.neutral)
    const uptime = medibles.length
      ? Math.round(
          (medibles.filter(s => s === STATUS.healthy).length /
            medibles.length) *
            100,
        )
      : null
    return {
      ...service,
      status,
      latency: Date.now() - t0,
      uptime,
      lastCheck: new Date(),
      history: newHistory,
      detail: null,
      error: null,
      extraData: null,
      versionInfo: null,
      migrador: null,
      etiqueta: null,
      ...extra,
    }
  }
  // 401 (sin sesión) y 403 (sin permiso) no son una caída del migrador: estado
  // neutro, sin sumar al resumen. validateStatus hace que axios RESUELVA todo
  // status HTTP: así el interceptor global de respuesta (que ante un 401 con
  // token vencido limpia la sesión y redirige al login) no corre desde esta
  // ruta pública. Sin token (visitante) ya no redirigía; con token vencido
  // evita sacar al usuario del dashboard. El comportamiento global no cambia.
  const porHttp = httpStatus => {
    if (httpStatus === 401)
      return finish(STATUS.neutral, { error: MIGRADOR_MSG_SIN_SESION })
    if (httpStatus === 403)
      return finish(STATUS.neutral, { error: MIGRADOR_MSG_SIN_PERMISO })
    return finish(STATUS.down, { error: MIGRADOR_MSG_SIN_RESPUESTA })
  }
  try {
    const res = await axios.get(url, {
      timeout: FETCH_TIMEOUT_MS,
      headers: { Accept: "application/json" },
      validateStatus: () => true,
    })
    if (res && typeof res.status === "number" && res.status >= 400) {
      return porHttp(res.status)
    }
    const d = res && res.data
    if (!d || typeof d !== "object" || !d.estado) {
      return finish(STATUS.down, { error: MIGRADOR_MSG_SIN_RESPUESTA })
    }
    const status = migradorEstadoToStatus(d.estado)
    const deshabilitado = d.estado === "DESHABILITADO"
    const mensaje =
      status === STATUS.healthy
        ? null
        : d.mensaje ||
          (deshabilitado
            ? MIGRADOR_MSG_DESHABILITADO
            : status === STATUS.unhealthy
              ? MIGRADOR_MSG_DEGRADADO
              : MIGRADOR_MSG_DOWN)
    return finish(status, {
      etiqueta: deshabilitado ? MIGRADOR_ETIQUETA_DESHABILITADO : null,
      versionInfo: d.version || null,
      migrador: {
        sha: migradorShortSha(d.gitSha),
        target: d.target || null,
        corriendo: d.corriendo === true,
        ultimaCorridaART: d.ultimaCorridaART || null,
        mensaje,
      },
    })
  } catch (err) {
    const httpStatus = err && err.response && err.response.status
    return porHttp(httpStatus)
  }
}

function buildInitialServices() {
  return SERVICES.map(s => ({
    ...s,
    status: STATUS.loading,
    latency: null,
    uptime: null,
    lastCheck: null,
    history: [],
    detail: null,
    error: null,
    versionInfo: null,
    migrador: null,
    dbStatus: null,
    dbType: null,
    diskTotal: null,
    diskFree: null,
  }))
}

function TrafficLight({ status }) {
  const isActive = s => status === s
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        background: "#0a0a0a",
        border: "2px solid #1f1f1f",
        borderRadius: "12px",
        padding: "8px 6px",
        alignItems: "center",
        boxShadow: "inset 0 2px 4px #000",
      }}
    >
      {[STATUS.down, STATUS.unhealthy, STATUS.healthy].map(s => (
        <div
          key={s}
          style={{
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            background: isActive(s) ? statusConfig[s].color : "#1a1a1a",
            boxShadow: isActive(s) ? statusConfig[s].glow : "none",
            transition: "all 0.4s ease",
          }}
        />
      ))}
    </div>
  )
}

function MiniHistory({ history }) {
  if (!history || !history.length) return <div style={{ height: "22px" }} />
  return (
    <div
      style={{
        display: "flex",
        gap: "2px",
        alignItems: "flex-end",
        height: "22px",
      }}
    >
      {history.map((s, i) => (
        <div
          key={i}
          style={{
            width: "6px",
            height:
              s === STATUS.healthy
                ? "18px"
                : s === STATUS.unhealthy
                  ? "11px"
                  : "5px",
            background: statusConfig[s] ? statusConfig[s].color : "#6b7280",
            borderRadius: "2px",
            opacity: 0.4 + (i / history.length) * 0.6,
            transition: "height 0.3s",
          }}
        />
      ))}
    </div>
  )
}

function formatDetailValue(val) {
  if (val === null || val === undefined) return "—"
  if (typeof val === "number") {
    if (val > 1073741824) return (val / 1073741824).toFixed(1) + " GB"
    if (val > 1048576) return (val / 1048576).toFixed(1) + " MB"
    return String(val)
  }
  return String(val)
}

// BDSJEA-89: info.app.version viene de @project.version@ (Maven), que en
// develop/qa resuelve a algo como "1.0.0-SNAPSHOT". Se recorta el sufijo solo
// al mostrar, sin tocar backend/pom/pipeline.
function formatVersion(v) {
  return v ? v.replace(/-SNAPSHOT$/i, "") : v
}

function ComponentDetail({ detail }) {
  var healthEntries = getHealthEntries(detail)
  if (!healthEntries) return null
  var entries = Object.entries(healthEntries)
  return (
    <div
      style={{
        marginTop: "10px",
        paddingTop: "10px",
        borderTop: "1px solid #1f1f1f",
      }}
    >
      {entries.map(function (entry) {
        var name = entry[0]
        var comp = entry[1]
        var s =
          comp && comp.status === "UP"
            ? STATUS.healthy
            : comp && comp.status === "UNKNOWN"
              ? STATUS.unhealthy
              : STATUS.down
        var c = statusConfig[s]
        var subDetails =
          comp && (comp.details || comp.components)
            ? Object.entries(comp.details || comp.components)
            : []
        return (
          <div
            key={name}
            style={{
              marginBottom: "6px",
              padding: "6px 8px",
              background: "#0a0a0a",
              borderRadius: "6px",
              border: "1px solid " + c.color + "33",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: subDetails.length ? "4px" : "0",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: c.color,
                  boxShadow: c.glow,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "10px",
                  fontFamily: "monospace",
                  fontWeight: "700",
                  color: c.color,
                  letterSpacing: "0.05em",
                }}
              >
                {name}
              </span>
              <span
                style={{
                  fontSize: "9px",
                  fontFamily: "monospace",
                  color: "#4b5563",
                  marginLeft: "auto",
                }}
              >
                {comp ? comp.status : "?"}
              </span>
            </div>
            {subDetails.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  marginLeft: "14px",
                }}
              >
                {subDetails.map(function (sd) {
                  return (
                    <span
                      key={sd[0]}
                      style={{
                        fontSize: "9px",
                        fontFamily: "monospace",
                        color: "#6b7280",
                      }}
                    >
                      {sd[0]}:{" "}
                      <span style={{ color: "#9ca3af" }}>
                        {formatDetailValue(sd[1])}
                      </span>
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ServiceCard({ service, isSelected, onClick }) {
  const cfg = statusConfig[service.status] || statusConfig.loading
  const timeSince = service.lastCheck
    ? `${Math.floor((Date.now() - service.lastCheck) / 1000)}s atrás`
    : "—"

  const estado = service.extraData
    ? {
        resultado: service.extraData.resultado || "—",
        tsRegistro: service.extraData.tsRegistro
          ? new Date(service.extraData.tsRegistro).toLocaleString("es-AR", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : null,
      }
    : null

  return (
    <div
      data-testid={`card-${service.id}`}
      onClick={onClick}
      style={{
        background: `linear-gradient(135deg, #111 0%, ${cfg.bg} 100%)`,
        border: `1px solid ${isSelected ? cfg.color : "#222"}`,
        borderLeft: `3px solid ${cfg.color}`,
        borderRadius: "8px",
        padding: "14px 16px",
        cursor: "pointer",
        transition: "all 0.25s ease",
        boxShadow: isSelected
          ? `0 0 20px ${cfg.color}22, 0 4px 12px #000`
          : "0 2px 8px #000",
        transform: isSelected ? "translateY(-1px)" : "none",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          pointerEvents: "none",
          backgroundImage:
            "repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 12px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 12px)",
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "10px",
        }}
      >
        <TrafficLight status={service.status} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: "13px",
              fontWeight: "700",
              color: "#e5e7eb",
              letterSpacing: "0.02em",
              overflowWrap: "anywhere",
            }}
          >
            {service.name}
          </div>
          <div
            style={{
              display: "flex",
              gap: "6px",
              marginTop: "4px",
              flexWrap: "wrap",
            }}
          >
            {service.dbStatus && (
              <span
                style={{
                  fontSize: "9px",
                  fontFamily: "monospace",
                  padding: "1px 5px",
                  borderRadius: "3px",
                  background: service.dbStatus === "UP" ? "#052e16" : "#450a0a",
                  color: service.dbStatus === "UP" ? "#22c55e" : "#ef4444",
                  border: `1px solid ${service.dbStatus === "UP" ? "#166534" : "#991b1b"}`,
                }}
              >
                DB: {service.dbStatus === "UP" ? "OK" : "FAIL"}
                {service.dbType ? ` (${service.dbType})` : ""}
              </span>
            )}
            {service.diskTotal != null && service.diskFree != null && (
              <span
                style={{
                  fontSize: "9px",
                  fontFamily: "monospace",
                  padding: "1px 5px",
                  borderRadius: "3px",
                  background:
                    service.diskFree / service.diskTotal > 0.1
                      ? "#052e16"
                      : "#450a0a",
                  color:
                    service.diskFree / service.diskTotal > 0.1
                      ? "#22c55e"
                      : "#ef4444",
                  border: `1px solid ${service.diskFree / service.diskTotal > 0.1 ? "#166534" : "#991b1b"}`,
                }}
              >
                DISK:{" "}
                {((service.diskFree / service.diskTotal) * 100).toFixed(1)}%
                free
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#6b7280",
              marginTop: "2px",
              fontFamily: "monospace",
            }}
          >
            {service.port != null ? `:${service.port} · ` : ""}
            {service.description}
          </div>
          {service.versionInfo && (
            <div
              style={{
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                fontSize: "10px",
                color: "#9ca3af",
                marginTop: "2px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              v{formatVersion(service.versionInfo)}
            </div>
          )}
          {service.migrador && (
            <div
              data-testid="migrador-detalle"
              style={{
                fontFamily: "monospace",
                fontSize: "10px",
                color: "#9ca3af",
                marginTop: "2px",
                lineHeight: "1.5",
              }}
            >
              <div>
                SHA: {service.migrador.sha || "-"} · Target:{" "}
                {service.migrador.target || "-"}
              </div>
              <div>
                En ejecución: {service.migrador.corriendo ? "sí" : "no"}
              </div>
              <div>
                Última corrida: {service.migrador.ultimaCorridaART || "-"}
              </div>
              {service.migrador.mensaje && (
                <div style={{ color: cfg.color }}>
                  {service.migrador.mensaje}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            fontFamily: "monospace",
            fontSize: "10px",
            fontWeight: "700",
            color: cfg.color,
            textShadow: cfg.glow,
            letterSpacing: "0.1em",
            whiteSpace: "nowrap",
          }}
        >
          {service.etiqueta || cfg.label}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ display: "flex", gap: "16px" }}>
          <div>
            <div
              style={{
                fontSize: "10px",
                color: "#4b5563",
                fontFamily: "monospace",
                marginBottom: "2px",
              }}
            >
              LATENCIA
            </div>
            <div
              style={{
                fontSize: "13px",
                fontFamily: "monospace",
                color:
                  service.latency == null
                    ? "#4b5563"
                    : service.latency >
                        (service.latencyWarnMs || LATENCY_WARN_MS)
                      ? "#f59e0b"
                      : cfg.color,
              }}
            >
              {service.latency != null ? `${service.latency}ms` : "—"}
              {service.latency != null &&
                service.latency >
                  (service.latencyWarnMs || LATENCY_WARN_MS) && (
                  <span
                    style={{
                      fontSize: "9px",
                      marginLeft: "4px",
                      color: "#f59e0b",
                    }}
                  >
                    ⚠ LENTO
                  </span>
                )}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "10px",
                color: "#4b5563",
                fontFamily: "monospace",
                marginBottom: "2px",
              }}
            >
              UPTIME
            </div>
            <div
              style={{
                fontSize: "13px",
                fontFamily: "monospace",
                color:
                  service.uptime > 90
                    ? "#22c55e"
                    : service.uptime > 70
                      ? "#f59e0b"
                      : "#ef4444",
              }}
            >
              {service.uptime != null ? `${service.uptime}%` : "—"}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "10px",
                color: "#4b5563",
                fontFamily: "monospace",
                marginBottom: "2px",
              }}
            >
              CHECK
            </div>
            <div
              style={{
                fontSize: "11px",
                fontFamily: "monospace",
                color: "#6b7280",
              }}
            >
              {timeSince}
            </div>
          </div>
          {estado && (
            <div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#4b5563",
                  fontFamily: "monospace",
                  marginBottom: "2px",
                }}
              >
                ULTIMA SINCRO
              </div>
              <div
                style={{
                  fontSize: "11px",
                  fontFamily: "monospace",
                  color: estado.resultado === "OK" ? "#22c55e" : "#ef4444",
                }}
              >
                {estado.resultado}
                {estado.tsRegistro && (
                  <span style={{ color: "#6b7280", marginLeft: "4px" }}>
                    {estado.tsRegistro}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        <MiniHistory history={service.history} />
      </div>

      {service.error && (
        <div
          style={{
            marginTop: "8px",
            fontSize: "10px",
            color: service.status === STATUS.neutral ? "#9ca3af" : "#ef4444",
            fontFamily: "monospace",
            opacity: 0.7,
          }}
        >
          {service.status === STATUS.neutral ? "ℹ" : "⚠"} {service.error}
        </div>
      )}

      {isSelected && <ComponentDetail detail={service.detail} />}
    </div>
  )
}

function SystemSummary({ services: allServices }) {
  const services = allServices.filter(function (s) {
    return !s.fueraDelResumen
  })
  const healthy = services.filter(function (s) {
    return s.status === STATUS.healthy
  }).length
  const unhealthy = services.filter(function (s) {
    return s.status === STATUS.unhealthy
  }).length
  const down = services.filter(function (s) {
    return s.status === STATUS.down
  }).length
  const loading = services.filter(function (s) {
    return s.status === STATUS.loading
  }).length

  const overallStatus =
    loading > 0
      ? STATUS.loading
      : down > 0
        ? STATUS.down
        : unhealthy > 0
          ? STATUS.unhealthy
          : STATUS.healthy
  const cfg = statusConfig[overallStatus]

  const messages = {
    loading: "CHEQUEANDO SERVICIOS...",
    healthy: "TODOS LOS SISTEMAS OPERATIVOS",
    unhealthy: `${unhealthy} SERVICIO${unhealthy > 1 ? "S" : ""} DEGRADADO${unhealthy > 1 ? "S" : ""}`,
    down: `${down} SERVICIO${down > 1 ? "S" : ""} CAÍDO${down > 1 ? "S" : ""}`,
  }

  return (
    <div
      style={{
        background: `linear-gradient(90deg, #0d0d0d, ${cfg.bg}, #0d0d0d)`,
        border: `1px solid ${cfg.color}44`,
        borderRadius: "10px",
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "24px",
        boxShadow: `0 0 30px ${cfg.color}11`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <div
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            background: cfg.color,
            boxShadow: cfg.glow,
            animation: "blink 1.5s ease-in-out infinite",
          }}
        />
        <div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "15px",
              fontWeight: "700",
              color: cfg.color,
              letterSpacing: "0.08em",
            }}
          >
            {messages[overallStatus]}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#6b7280",
              fontFamily: "monospace",
              marginTop: "2px",
            }}
          >
            Sistema Ewan Agro · {services.length} microservicios · ENV:{" "}
            {ENV_LABEL}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: "20px" }}>
        {[
          { label: "ONLINE", count: healthy, color: "#22c55e" },
          { label: "DEGRADADO", count: unhealthy, color: "#f59e0b" },
          { label: "CAÍDO", count: down, color: "#ef4444" },
        ].map(function (item) {
          return (
            <div key={item.label} style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "22px",
                  fontFamily: "monospace",
                  fontWeight: "700",
                  color: item.color,
                  lineHeight: 1,
                }}
              >
                {item.count}
              </div>
              <div
                style={{
                  fontSize: "9px",
                  color: "#4b5563",
                  letterSpacing: "0.1em",
                  fontFamily: "monospace",
                }}
              >
                {item.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

TrafficLight.propTypes = { status: PropTypes.string.isRequired }
MiniHistory.propTypes = { history: PropTypes.array }
ComponentDetail.propTypes = { detail: PropTypes.object }
ServiceCard.propTypes = {
  service: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
}
SystemSummary.propTypes = { services: PropTypes.array.isRequired }

export default function ServiceHealthDashboard() {
  const [services, setServices] = useState(buildInitialServices)
  const [selected, setSelected] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const refresh = useCallback(function (currentServices) {
    setRefreshing(true)
    Promise.all(
      SERVICES.map(function (svc) {
        const prev = currentServices.find(function (s) {
          return s.id === svc.id
        })
        const history = prev ? prev.history : []
        return svc.kind === "migrador"
          ? fetchMigradorHealth(svc, history)
          : fetchServiceHealth(svc, history, null)
      }),
    )
      .then(function (results) {
        setServices(results)
        setLastRefresh(new Date())
        setCountdown(AUTO_REFRESH_SECONDS)
      })
      .finally(function () {
        setRefreshing(false)
      })
  }, [])

  useEffect(
    function () {
      refresh(buildInitialServices())
    },
    [refresh],
  )

  useEffect(
    function () {
      if (!autoRefresh) return
      const interval = setInterval(function () {
        setCountdown(function (c) {
          if (c <= 1) {
            setServices(function (current) {
              refresh(current)
              return current
            })
            return AUTO_REFRESH_SECONDS
          }
          return c - 1
        })
      }, 1000)
      return function () {
        clearInterval(interval)
      }
    },
    [autoRefresh, refresh],
  )

  function handleManualRefresh() {
    setServices(function (current) {
      refresh(current)
      return current
    })
  }

  const groupedServices = {
    core: services.filter(function (s) {
      return s.group === "core"
    }),
    agro: services.filter(function (s) {
      return s.group === "agro"
    }),
    migracion: services.filter(function (s) {
      return s.group === "migracion"
    }),
  }
  const groupLabels = {
    core: "NÚCLEO",
    agro: "AGROPECUARIO",
    migracion: "MIGRACIÓN",
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#080808",
        color: "#e5e7eb",
        fontFamily: "monospace",
        padding: "0",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes spin { to{transform:rotate(360deg)} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; background: #111; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }
      `}</style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 100,
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)",
        }}
      />

      <div
        style={{
          borderBottom: "1px solid #1a1a1a",
          padding: "16px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(180deg, #0f0f0f, #080808)",
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "3px",
              background: "#111",
              border: "1px solid #222",
              borderRadius: "6px",
              padding: "6px 8px",
            }}
          >
            {[STATUS.down, STATUS.unhealthy, STATUS.healthy].map(function (s) {
              return (
                <div
                  key={s}
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: statusConfig[s].color,
                    boxShadow: statusConfig[s].glow,
                    animation: "blink 2s infinite",
                    animationDelay:
                      s === STATUS.healthy
                        ? "0s"
                        : s === STATUS.unhealthy
                          ? "0.3s"
                          : "0.6s",
                  }}
                />
              )
            })}
          </div>
          <div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "700",
                letterSpacing: "0.15em",
                fontFamily: "'JetBrains Mono', monospace",
                background: "linear-gradient(90deg, #e5e7eb, #9ca3af)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              EWAN AGRO · MONITOR DE SERVICIOS
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#4b5563",
                letterSpacing: "0.2em",
                marginTop: "2px",
              }}
            >
              SISTEMA DE GESTIÓN AGROPECUARIA · ENV: {ENV_LABEL}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{ fontSize: "11px", color: "#4b5563", textAlign: "right" }}
          >
            <div>
              ÚLTIMO REFRESH:{" "}
              {lastRefresh ? lastRefresh.toLocaleTimeString("es-AR") : "—"}
            </div>
            {autoRefresh && (
              <div style={{ color: countdown <= 5 ? "#f59e0b" : "#4b5563" }}>
                PRÓXIMO EN {countdown}s
              </div>
            )}
          </div>
          <button
            onClick={function () {
              setAutoRefresh(function (a) {
                return !a
              })
            }}
            style={{
              background: autoRefresh ? "#052e16" : "#111",
              border: `1px solid ${autoRefresh ? "#22c55e44" : "#333"}`,
              color: autoRefresh ? "#22c55e" : "#6b7280",
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "10px",
              letterSpacing: "0.1em",
              fontFamily: "monospace",
            }}
          >
            AUTO {autoRefresh ? "ON" : "OFF"}
          </button>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            style={{
              background: refreshing ? "#111" : "#0f172a",
              border: "1px solid #334155",
              color: refreshing ? "#4b5563" : "#94a3b8",
              padding: "6px 14px",
              borderRadius: "6px",
              cursor: refreshing ? "default" : "pointer",
              fontSize: "10px",
              letterSpacing: "0.1em",
              fontFamily: "monospace",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span
              style={{
                display: "inline-block",
                animation: refreshing ? "spin 0.6s linear infinite" : "none",
              }}
            >
              ↻
            </span>
            REFRESH
          </button>
        </div>
      </div>

      <div style={{ padding: "28px 32px" }}>
        <SystemSummary services={services} />

        {Object.entries(groupedServices).map(function (entry) {
          const group = entry[0]
          const svcs = entry[1]
          return (
            <div key={group} style={{ marginBottom: "28px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    width: "3px",
                    height: "16px",
                    background: "#333",
                    borderRadius: "2px",
                  }}
                />
                <span
                  style={{
                    fontSize: "10px",
                    color: "#4b5563",
                    letterSpacing: "0.2em",
                    fontFamily: "monospace",
                  }}
                >
                  {groupLabels[group]}
                </span>
                <div
                  style={{ flex: 1, height: "1px", background: "#1a1a1a" }}
                />
                <span
                  style={{
                    fontSize: "10px",
                    color: "#374151",
                    fontFamily: "monospace",
                  }}
                >
                  {
                    svcs.filter(function (s) {
                      return s.status === STATUS.healthy
                    }).length
                  }
                  /{svcs.length} online
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "10px",
                }}
              >
                {svcs.map(function (service) {
                  return (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      isSelected={selected === service.id}
                      onClick={function () {
                        setSelected(function (prev) {
                          return prev === service.id ? null : service.id
                        })
                      }}
                    />
                  )
                })}
              </div>
            </div>
          )
        })}

        <div
          style={{
            marginTop: "8px",
            padding: "10px 16px",
            background: "#0f172a",
            border: "1px solid #1e293b",
            borderRadius: "6px",
            fontSize: "10px",
            color: "#475569",
            fontFamily: "monospace",
            lineHeight: "1.6",
          }}
        >
          {"💡 "}
          <strong style={{ color: "#64748b" }}>CORS:</strong>
          {" Si los fetch fallan, agregá en cada "}
          <span style={{ color: "#94a3b8" }}>application.properties</span>
          {": "}
          <span style={{ color: "#22d3ee" }}>
            management.endpoints.web.cors.allowed-origins=http://localhost:3000
          </span>
          {" — o configurá un proxy en "}
          <span style={{ color: "#94a3b8" }}>src/setupProxy.js</span>
          {"."}
        </div>

        <div
          style={{
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: "1px solid #111",
            display: "flex",
            justifyContent: "space-between",
            fontSize: "10px",
            color: "#374151",
            fontFamily: "monospace",
            letterSpacing: "0.05em",
          }}
        >
          <span>EWAN-AGRO · DASHBOARD v2.0.0</span>
          <span>
            {
              "● VERDE: <200ms · ● AMARILLO: 200-500ms · ● ROJO: >500ms / SIN RESPUESTA"
            }
          </span>
          <span>
            {new Date().toLocaleDateString("es-AR", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      </div>
    </div>
  )
}
